// Decides where each keyword label goes: which side of its bubble, and how far out.
// Everything here works in screen pixels against the chart's real plot area, so it behaves the same with dynamic axes, the fixed 0–100 axes and the animation pages' fixed ranges.

export type StraightSide = "top" | "bottom" | "left" | "right";
export type DiagonalSide = "topRight" | "topLeft" | "bottomRight" | "bottomLeft";
export type LabelSide = StraightSide | DiagonalSide;

/** A bubble's centre and radius, in canvas pixels. */
export interface ScreenBubble {
  x: number;
  y: number;
  r: number;
}

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface LabelSize {
  width: number;
  height: number;
}

/** Where a label goes: the side of its bubble, and the gap from the anchor point (datalabels' `offset`). */
export interface LabelPlacement {
  side: LabelSide;
  offset: number;
}

// Label styling lives here so the chart options and the size measurement can't drift apart.
export const LABEL_FONT = {
  size: 10,
  weight: 600,
  lineHeight: 1.2,
  family: "'Google Sans Flex', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
};
export const LABEL_PADDING = { top: 2, bottom: 2, left: 4, right: 4 };
export const LABEL_BORDER_WIDTH = 1;

/** The CSS font string chartjs-plugin-datalabels draws labels with. */
export const LABEL_FONT_CSS = `${LABEL_FONT.weight} ${LABEL_FONT.size}px ${LABEL_FONT.family}`;

/** The label frame datalabels draws for a text of this width: text + padding + border. */
export const labelSizeForText = (textWidth: number): LabelSize => ({
  width: textWidth + LABEL_PADDING.left + LABEL_PADDING.right + LABEL_BORDER_WIDTH * 2,
  height:
    LABEL_FONT.size * LABEL_FONT.lineHeight +
    LABEL_PADDING.top +
    LABEL_PADDING.bottom +
    LABEL_BORDER_WIDTH * 2,
});

// Order matters: ties go to the earlier side, and the variety bonus is keyed on a straight side's index.
const STRAIGHT_SIDES: StraightSide[] = ["top", "bottom", "left", "right"];
const DIAGONAL_SIDES: DiagonalSide[] = ["topRight", "topLeft", "bottomRight", "bottomLeft"];

const D = Math.SQRT1_2;

/** Unit vector for each side, in screen space (y grows downward). */
const SIDE_DIRECTION: Record<LabelSide, { x: number; y: number }> = {
  top: { x: 0, y: -1 },
  bottom: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  topRight: { x: D, y: -D },
  topLeft: { x: -D, y: -D },
  bottomRight: { x: D, y: D },
  bottomLeft: { x: -D, y: D },
};

// chartjs-plugin-datalabels reads a numeric `align` as degrees, measured clockwise from "to the right of the point".
const DIAGONAL_ALIGN_DEGREES: Record<DiagonalSide, number> = {
  topRight: 315,
  topLeft: 225,
  bottomRight: 45,
  bottomLeft: 135,
};

const isStraight = (side: LabelSide): side is StraightSide =>
  (STRAIGHT_SIDES as LabelSide[]).includes(side);

// Diagonals have a top/bottom component, so they get the same spacing as top and bottom labels.
const isVertical = (side: LabelSide): boolean => side !== "left" && side !== "right";

/** The value for datalabels' `align` option. */
export const sideToAlign = (side: LabelSide): StraightSide | number =>
  isStraight(side) ? side : DIAGONAL_ALIGN_DEGREES[side];

/** The value for datalabels' `anchor` option, sized to keep labels off larger bubbles. */
export const labelAnchor = (side: LabelSide, radius: number): "end" | "center" => {
  if (radius > 15) return "end";
  if (radius > 8) return isVertical(side) ? "end" : "center";
  return "center";
};

/** The usual gap between the anchor point and the label, before any stacking. */
export const labelOffset = (side: LabelSide, radius: number): number => {
  const base = Math.max(6, radius * 0.5);
  return isVertical(side) && radius > 12 ? base + 4 : base;
};

// When every spot beside a bubble is taken, a label with a top/bottom component may step further out by whole label heights.
const MAX_STACK_STEPS = 2;
const STACK_GAP = 2;

// Each step lifts (or drops) the label by one label height plus a gap; a diagonal needs a longer offset to move that far vertically.
const stackedOffset = (side: LabelSide, radius: number, size: LabelSize, steps: number): number => {
  const offset = labelOffset(side, radius);
  if (steps === 0) return offset;
  return offset + (steps * (size.height + STACK_GAP)) / Math.abs(SIDE_DIRECTION[side].y);
};

/**
 * The frame datalabels will draw for this placement, mirroring its own positioning math.
 * On a vertical axis, datalabels puts an "end" anchor on the bubble's top edge whatever side the label is on.
 * The box then sits beyond that anchor along the side's direction: pushed out by half its size (scaled so a diagonal clears a full corner), plus the offset.
 */
export const labelBox = (bubble: ScreenBubble, placement: LabelPlacement, size: LabelSize): Box => {
  const direction = SIDE_DIRECTION[placement.side];
  const anchorY = labelAnchor(placement.side, bubble.r) === "end" ? bubble.y - bubble.r : bubble.y;
  const scale = 1 / Math.max(Math.abs(direction.x), Math.abs(direction.y));

  const centerX = bubble.x + (size.width / 2) * direction.x * scale + placement.offset * direction.x;
  const centerY = anchorY + (size.height / 2) * direction.y * scale + placement.offset * direction.y;

  return {
    left: centerX - size.width / 2,
    right: centerX + size.width / 2,
    top: centerY - size.height / 2,
    bottom: centerY + size.height / 2,
  };
};

/** How many pixels of the box fall outside the plot area; 0 means it fits. */
export const overflow = (box: Box, area: Box): number =>
  Math.max(0, area.left - box.left) +
  Math.max(0, box.right - area.right) +
  Math.max(0, area.top - box.top) +
  Math.max(0, box.bottom - area.bottom);

export const boxesOverlap = (a: Box, b: Box): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

// True when a neighbour at (dx, dy) from the bubble lies in the direction the side points.
const pointsToward = (side: LabelSide, dx: number, dy: number): boolean => {
  const direction = SIDE_DIRECTION[side];
  const alongX = direction.x === 0 || Math.sign(dx) === Math.sign(direction.x);
  const alongY = direction.y === 0 || Math.sign(dy) === Math.sign(direction.y);
  return alongX && alongY;
};

// Neighbours closer than this (as a fraction of the plot's width/height) count as crowding.
const PROXIMITY_THRESHOLD = 0.15;
const VERY_CLOSE_THRESHOLD = 0.08;

// Diagonals start behind the straight sides, so they only win when the straight sides are crowded or don't fit.
const DIAGONAL_BASE_SCORE = -3;

// Each stacking step costs a little, so a label only moves away from its bubble to dodge an overlap.
const STACK_STEP_PENALTY = 6;

// Landing on a label that's already placed outweighs any side preference or stacking, but not the plot edge.
const LABEL_OVERLAP_PENALTY = 20;

// A placement that would push the label out of the plot is ruled out, unless every placement would.
const OVERFLOW_PENALTY = 1000;

/** Picks the best placement for bubble `index`'s label, given every keyword bubble on screen and the labels placed so far. */
export const chooseLabelPlacement = ({
  index,
  bubbles,
  area,
  labelSize,
  placedLabels = [],
}: {
  index: number;
  bubbles: ScreenBubble[];
  area: Box;
  labelSize: LabelSize;
  placedLabels?: Box[];
}): LabelPlacement => {
  const current = bubbles[index];
  if (!current) return { side: "top", offset: labelOffset("top", 0) };

  const areaWidth = area.right - area.left || 1;
  const areaHeight = area.bottom - area.top || 1;

  // The long-standing preferences for a side: avoid nearby bubbles, spread labels out, suit the bubble's size.
  const sideScore = (side: LabelSide): number => {
    let score = 0;

    bubbles.forEach((other, i) => {
      if (i === index) return;
      const dx = other.x - current.x;
      const dy = other.y - current.y;
      const distance = Math.hypot(dx / areaWidth, dy / areaHeight);
      if (distance >= PROXIMITY_THRESHOLD) return;

      if (pointsToward(side, dx, dy)) score -= 10;
      if (distance < VERY_CLOSE_THRESHOLD) score -= 5;
    });

    if (!isStraight(side)) return score + DIAGONAL_BASE_SCORE;

    // Spread neighbouring labels across different sides.
    score += (index + STRAIGHT_SIDES.indexOf(side)) % 4;

    // Big bubbles read better with labels above/below, small ones beside.
    if (current.r > 15) {
      if (side === "top" || side === "bottom") score += 3;
    } else if (side === "left" || side === "right") {
      score += 2;
    }
    return score;
  };

  // Every side at its usual gap, then the stacked steps for sides with a top/bottom component.
  // Order matters: ties go to the earlier candidate, so the usual gap and the straight sides win ties.
  const candidates: { side: LabelSide; steps: number }[] = [];
  for (let steps = 0; steps <= MAX_STACK_STEPS; steps++) {
    [...STRAIGHT_SIDES, ...DIAGONAL_SIDES].forEach((side) => {
      if (steps === 0 || isVertical(side)) candidates.push({ side, steps });
    });
  }

  const scored = candidates.map(({ side, steps }) => {
    const placement = { side, offset: stackedOffset(side, current.r, labelSize, steps) };
    const box = labelBox(current, placement, labelSize);
    const overlaps = placedLabels.filter((placed) => boxesOverlap(box, placed)).length;
    const outside = overflow(box, area);

    let score = sideScore(side) - STACK_STEP_PENALTY * steps - LABEL_OVERLAP_PENALTY * overlaps;
    if (outside > 0) score -= OVERFLOW_PENALTY + outside;
    return { placement, score };
  });

  return scored.sort((a, b) => b.score - a.score)[0].placement;
};

/** Places every label in order, each one steering clear of the labels placed before it. */
export const layoutLabels = ({
  bubbles,
  labelSizes,
  area,
}: {
  bubbles: ScreenBubble[];
  labelSizes: LabelSize[];
  area: Box;
}): LabelPlacement[] => {
  const placedLabels: Box[] = [];
  return bubbles.map((bubble, index) => {
    const labelSize = labelSizes[index];
    const placement = chooseLabelPlacement({ index, bubbles, area, labelSize, placedLabels });
    placedLabels.push(labelBox(bubble, placement, labelSize));
    return placement;
  });
};
